/**
 * ContentEngineSpendTile — month-to-date LLM spend rollup for the content
 * engine. Surfaces total estimated $ + call count + per-model breakdown so
 * admins can spot a budget squeeze before the next Generate click fails.
 *
 * Cost-safe: pure DB rollup of `ai_provider_metadata.char_in/out` × per-token
 * rates. No live LLM calls.
 */
import { useEffect, useState } from "react";
import {
    DollarSign,
    AlertTriangle,
    Activity,
    PackageOpen,
    RefreshCw,
    Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt$(n) {
    if (n === null || n === undefined) return "—";
    return `$${Number(n).toFixed(n < 1 ? 3 : 2)}`;
}

export default function ContentEngineSpendTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/admin/content-engine/spend-summary`,
                { withCredentials: true },
            );
            setData(r.data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load spend summary.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading && !data) {
        return (
            <div
                className="bl-card p-5 flex items-center gap-3 text-sm text-bl-muted"
                data-testid="ce-spend-tile-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading content-engine spend…
            </div>
        );
    }
    if (!data) return null;

    const mtd = data.mtd || {};
    const d7 = data.d7 || {};
    const d30 = data.d30 || {};
    const byModel = mtd.by_model || [];
    const monthName = new Date(mtd.started_at || Date.now()).toLocaleDateString(
        undefined,
        { month: "long", year: "numeric" },
    );

    return (
        <div
            className="bl-card p-5 space-y-4"
            data-testid="ce-spend-tile"
        >
            {/* Budget warning banner — only renders when amber/red */}
            {data.budget && data.budget.level !== "ok" && (
                <div
                    className={
                        "rounded border p-3 flex items-start gap-3 " +
                        (data.budget.level === "red"
                            ? "border-[var(--bl-rust)] bg-[var(--bl-rust-tint)]"
                            : "border-[var(--bl-amber)] bg-[var(--bl-amber-tint)]")
                    }
                    data-testid="ce-spend-budget-banner"
                >
                    <AlertTriangle
                        className={
                            "h-4 w-4 mt-0.5 flex-shrink-0 " +
                            (data.budget.level === "red"
                                ? "text-[var(--bl-rust)]"
                                : "text-[var(--bl-amber)]")
                        }
                    />
                    <div className="flex-1 text-xs leading-relaxed">
                        <div className="font-medium text-bl mb-0.5">
                            {data.budget.level === "red"
                                ? "Monthly content-engine budget nearly exhausted"
                                : "Approaching monthly content-engine budget"}
                        </div>
                        <div className="text-bl-muted">
                            {fmt$(mtd.cost_usd)} of {fmt$(data.budget.monthly_usd)} burnt
                            ({data.budget.pct_burnt}%). {fmt$(data.budget.remaining_usd)} remaining.
                            {data.budget.level === "red"
                                ? " Top up your Universal Key or pause further generations."
                                : " Plan upcoming generations carefully."}
                        </div>
                        <div className="mt-1.5 h-1.5 bg-bl-bg rounded-full overflow-hidden">
                            <div
                                className={
                                    "h-full " +
                                    (data.budget.level === "red"
                                        ? "bg-[var(--bl-rust)]"
                                        : "bg-[var(--bl-amber)]")
                                }
                                style={{ width: `${Math.min(100, data.budget.pct_burnt)}%` }}
                            />
                        </div>
                        <div className="mt-1 text-[10px] text-bl-muted italic">
                            Configurable via{" "}
                            <code className="text-[10px]">CONTENT_ENGINE_MONTHLY_BUDGET_USD</code>{" "}
                            env var.
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                        Content engine spend · {monthName}
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="bl-btn-ghost text-xs gap-1"
                    disabled={loading}
                    data-testid="ce-spend-refresh"
                >
                    <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        MTD spend
                    </div>
                    <div
                        className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl"
                        data-testid="ce-spend-mtd"
                    >
                        {fmt$(mtd.cost_usd)}
                    </div>
                    <div className="text-[11px] text-bl-muted mt-0.5">
                        {mtd.packages || 0} pkgs · {mtd.calls || 0} calls
                    </div>
                    {data.budget && (
                        <div className="mt-2" data-testid="ce-spend-budget-bar">
                            <div className="h-1 bg-bl-bg rounded-full overflow-hidden">
                                <div
                                    className={
                                        "h-full " +
                                        (data.budget.level === "red"
                                            ? "bg-[var(--bl-rust)]"
                                            : data.budget.level === "amber"
                                              ? "bg-[var(--bl-amber)]"
                                              : "bg-bl-primary")
                                    }
                                    style={{
                                        width: `${Math.min(100, data.budget.pct_burnt)}%`,
                                    }}
                                />
                            </div>
                            <div className="text-[10px] text-bl-muted mt-1">
                                {data.budget.pct_burnt}% of {fmt$(data.budget.monthly_usd)} budget
                            </div>
                        </div>
                    )}
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Last 30d
                    </div>
                    <div className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl">
                        {fmt$(d30.cost_usd)}
                    </div>
                    <div className="text-[11px] text-bl-muted mt-0.5">
                        {d30.packages || 0} pkgs · {d30.calls || 0} calls
                    </div>
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Last 7d
                    </div>
                    <div className="font-display font-semibold text-2xl text-bl mt-1 font-mono-bl">
                        {fmt$(d7.cost_usd)}
                    </div>
                    <div className="text-[11px] text-bl-muted mt-0.5">
                        {d7.packages || 0} pkgs · {d7.calls || 0} calls
                    </div>
                </div>
                <div className="bl-card p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Failed (MTD)
                    </div>
                    <div
                        className={
                            "font-display font-semibold text-2xl mt-1 font-mono-bl " +
                            ((mtd.failed || 0) > 0
                                ? "text-[var(--bl-rust)]"
                                : "text-bl")
                        }
                        data-testid="ce-spend-failed"
                    >
                        {mtd.failed || 0}
                    </div>
                    <div className="text-[11px] text-bl-muted mt-0.5">
                        of {mtd.calls || 0} calls
                    </div>
                </div>
            </div>

            {byModel.length > 0 && (
                <div
                    className="rounded border border-bl/40"
                    data-testid="ce-spend-by-model"
                >
                    <div className="px-3 py-2 border-b border-bl/30 text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-1">
                        <Activity className="h-3 w-3" />
                        Spend by model · this month
                    </div>
                    {byModel.map((m) => {
                        const pct = mtd.cost_usd
                            ? Math.round((m.cost_usd / mtd.cost_usd) * 100)
                            : 0;
                        return (
                            <div
                                key={m.model}
                                className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3"
                            >
                                <div className="text-xs font-medium text-bl flex-1 truncate font-mono-bl">
                                    {m.model}
                                </div>
                                <div className="w-32 hidden sm:block">
                                    <div className="h-1.5 bg-bl-bg rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-bl-primary"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="text-xs text-bl-muted w-10 text-right">
                                    {pct}%
                                </div>
                                <div className="text-xs text-bl-muted w-14 text-right">
                                    {m.calls} call{m.calls === 1 ? "" : "s"}
                                </div>
                                <div className="text-xs text-bl font-medium font-mono-bl w-16 text-right">
                                    {fmt$(m.cost_usd)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {data.last_5_packages?.length > 0 && (
                <div className="rounded border border-bl/40" data-testid="ce-spend-recent">
                    <div className="px-3 py-2 border-b border-bl/30 text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-1">
                        <PackageOpen className="h-3 w-3" />
                        Recent packages
                    </div>
                    {data.last_5_packages.map((p) => (
                        <div
                            key={p.package_id}
                            className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3 text-xs"
                        >
                            <div className="text-bl flex-1 truncate" title={p.title}>
                                {p.title}
                            </div>
                            <div className="text-bl-muted w-20 text-right">
                                {p.calls} calls
                            </div>
                            <div className="text-bl font-medium font-mono-bl w-16 text-right">
                                {fmt$(p.cost_usd)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-[10px] text-bl-muted italic leading-relaxed">
                {data.estimate_disclaimer}
            </p>
        </div>
    );
}
